"use client";

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { SLIDE_W, SLIDE_H, type SpeciesSlideData } from "@/lib/carousel/types";
import { minimalSpeciesLayout, type MinimalTextBox } from "@/lib/carousel/templates/minimal-light";

export interface MinimalLightSpeciesOverlayProps {
  data: SpeciesSlideData;
  fonts: { display: string; body: string };
  update: (patch: Partial<{ namePrimary: string; nameSecondary: string; nameTertiary: string }>) => void;
}

function boxStyle(box: MinimalTextBox, fonts: { display: string; body: string }): CSSProperties {
  return {
    position: "absolute",
    left: "50%",
    top: box.top,
    transform: "translate(-50%, -50%)",
    width: SLIDE_W - 240,
    textAlign: "center",
    fontFamily: box.family === "display" ? fonts.display : fonts.body,
    fontSize: box.fontSize,
    fontWeight: box.fontWeight,
    color: box.color,
  };
}

/** Same overlay pattern as FieldDiarySpeciesOverlay, for Minimal Light's
 * centered caption band — positioned via minimalSpeciesLayout(), the same
 * source of truth minimal-light.ts's drawSpecies uses. */
export default function MinimalLightSpeciesOverlay({ data, fonts, update }: MinimalLightSpeciesOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(360 / SLIDE_W);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / SLIDE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { primary, secondary, tertiary } = minimalSpeciesLayout(data);

  return (
    <div ref={wrapRef} className="ced-text-overlay">
      <div
        className="ced-text-overlay-inner"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})` }}
      >
        {primary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(primary, fonts)}
            value={data.namePrimary ?? ""}
            onChange={(e) => update({ namePrimary: e.target.value })}
            aria-label="Primary name"
          />
        )}
        {secondary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(secondary, fonts)}
            value={data.nameSecondary ?? ""}
            onChange={(e) => update({ nameSecondary: e.target.value })}
            aria-label="Secondary name"
          />
        )}
        {tertiary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(tertiary, fonts)}
            value={data.nameTertiary}
            onChange={(e) => update({ nameTertiary: e.target.value })}
            placeholder="Name"
            aria-label="Name"
          />
        )}
      </div>
    </div>
  );
}
